import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { extractError } from "../lib/format";
import { Sparkles, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const GRADE_LABEL = {
  excellent: "Excellent",
  good: "Good work",
  attempted: "Nice try",
};

const REVIEW_PILL = {
  reviewed: { label: "Tutor reviewed", icon: ShieldCheck, cls: "ftg-review-pill--ok" },
  flagged:  { label: "Tutor flagged",  icon: AlertTriangle, cls: "ftg-review-pill--flag" },
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function MySubmissions() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/submissions")
      .then(({ data }) => {
        if (alive) setItems(data.items || []);
      })
      .catch((e) => {
        if (alive) setErr(extractError(e, "Could not load submissions."));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="ftg-subs__empty" data-testid="subs-loading">
        <Loader2 size={18} className="ftg-spin" /> Loading your submissions…
      </div>
    );
  }
  if (err) {
    return (
      <div className="ftg-subs__empty" data-testid="subs-error">
        {err}
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="ftg-subs__empty" data-testid="subs-empty">
        You haven&apos;t submitted any homework yet. Open a lesson and try the &ldquo;Submit your homework&rdquo; section.
      </div>
    );
  }

  return (
    <div className="ftg-subs" data-testid="subs-list">
      {items.map((s) => {
        const pill = REVIEW_PILL[s.review_status];
        const PillIcon = pill?.icon;
        return (
          <div key={s.id} className="ftg-subs__row" data-testid={`sub-row-${s.id}`}>
            <div className="ftg-subs__row-info">
              <div className="ftg-subs__row-meta">
                Term {s.term} · Week {String(s.week).padStart(2, "0")} · {formatWhen(s.submitted_at)}
              </div>
              <Link
                to={`/lesson/${encodeURIComponent(s.lesson_id)}`}
                className="ftg-subs__row-title"
                data-testid={`sub-lesson-link-${s.id}`}
              >
                {s.lesson_title || s.lesson_id}
              </Link>
              {s.ai_feedback && (
                <p className="ftg-subs__row-feedback" data-testid={`sub-feedback-${s.id}`}>
                  {s.ai_feedback}
                </p>
              )}
              {s.review_status === "flagged" && s.admin_notes && (
                <div className="ftg-tutor-note" data-testid={`sub-admin-note-${s.id}`}>
                  <strong>A note from your tutor:</strong> {s.admin_notes}
                </div>
              )}
              {s.review_status === "reviewed" && s.admin_notes && (
                <div className="ftg-tutor-note ftg-tutor-note--ok" data-testid={`sub-admin-note-${s.id}`}>
                  <strong>A note from your tutor:</strong> {s.admin_notes}
                </div>
              )}
            </div>
            <div className="ftg-subs__row-side">
              {pill && (
                <span
                  className={`ftg-review-pill ${pill.cls}`}
                  data-testid={`sub-review-pill-${s.id}`}
                >
                  <PillIcon size={12} /> {pill.label}
                </span>
              )}
              {s.ai_grade && (
                <span
                  className={`ftg-hw__chip ftg-hw__chip--${s.ai_grade}`}
                  data-testid={`sub-grade-${s.id}`}
                >
                  <Sparkles size={12} /> {GRADE_LABEL[s.ai_grade] || s.ai_grade}
                </span>
              )}
              <span className="ftg-subs__row-count">{s.image_count} image{s.image_count === 1 ? "" : "s"}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
